import React, { useState } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import { CalendarCheck, CalendarX } from 'lucide-react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';

import { updateEventParticipant } from '../../utils/api';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

const responseButton = (colorKey) => (theme) => {
  const color = theme.palette[colorKey].main;
  return {
    m: 0,
    color,
    borderColor: color,
    boxShadow: `0 4px 0 ${darken(color, 0.58)}`,
    '&:hover': {
      color,
      borderColor: color,
      backgroundColor: alpha(color, 0.12),
      transform: 'translate(-1px, -1px)',
    },
    '&:active': { transform: 'translateY(3px)', boxShadow: 'none' },
  };
};

export default function EventInvitationCard({ invitation, onResolved }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const event = invitation.event;
  const startsAt = dayjs(event.starts_at).tz(event.timezone);
  const cancelled = event.status === 'cancelled';

  const respond = async (status) => {
    setSaving(true);
    setError('');
    try {
      await updateEventParticipant(invitation.id, { status }, invitation.revision);
      onResolved?.(invitation, status);
    } catch (responseError) {
      setError(responseError.data?.message || responseError.message || 'Unable to update the invitation.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      component="article"
      sx={(theme) => ({
        p: 2,
        border: `3px solid ${theme.palette.secondary.main}`,
        borderRadius: 4,
        background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.12)}, ${theme.palette.background.paper})`,
        boxShadow: `0 7px 0 ${alpha(theme.palette.secondary.main, 0.24)}`,
      })}
    >
      <Stack spacing={1.5}>
        <Typography variant="overline" sx={{ color: 'secondary.main' }}>Event invitation</Typography>
        <Typography variant="h6" sx={{ color: 'primary.main' }}>{event.title}</Typography>
        <Typography variant="body2" sx={{ color: 'primary.main', textAlign: 'left' }}>
          {startsAt.format('dddd, MMMM D · h:mm A')} · {event.timezone}
        </Typography>
        {invitation.invited_by && (
          <Typography variant="caption" sx={{ color: 'secondary.main' }}>
            Invited by {invitation.invited_by.username}
          </Typography>
        )}
        {error && <Alert severity="error" sx={{ color: 'secondary.main' }}>{error}</Alert>}
        {cancelled ? (
          <Alert severity="warning">This event was cancelled.</Alert>
        ) : (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end">
            <Button
              variant="outlined"
              startIcon={<CalendarX size={17} />}
              disabled={saving}
              onClick={() => respond('declined')}
              sx={responseButton('secondary')}
            >
              Decline
            </Button>
            <Button
              variant="outlined"
              startIcon={<CalendarCheck size={17} />}
              disabled={saving}
              onClick={() => respond('accepted')}
              sx={responseButton('primary')}
            >
              Accept
            </Button>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
